import { Auth } from 'aws-amplify';
import { createRouter, createWebHistory } from 'vue-router';

import {
  v2RedirectionNavGuard,
  shouldRedirectToV2,
} from '../helpers/v2Redirection';
import { navItems } from '../helpers/navItems';
import { unsetAuthTokenCookies } from '../helpers/token_extraction';
import { unsetAssumedUserOrgCookies } from '../helpers/v2AuthLoader';
import { useRootStore } from '../stores/root.js';
import { mobileAuthNavGuard } from '../composables/mobile_auth';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: () => import('./NothingHere.vue'),
    },
  ],
  publicRoutes: [],
});

router.beforeEach(mobileAuthNavGuard);

// router.beforeEach((_to, _from, next) => {
//   const { user } = useRootStore();
//   if (!user) window.sendMessageToApp?.('appLoaded', {});
//   next();
// });

router.beforeEach(async (to) => {
  // opens chat if the openChat query param is present
  const queryParams = { ...to.query };
  if (queryParams.openChat) {
    const store = useRootStore();
    store.chatOpened = true;
    if (queryParams.groupId) {
      store.chatGroupId = queryParams.groupId;
    }
    if (queryParams.timestamp) {
      store.chatTimestamp = queryParams.timestamp;
    }
    if (queryParams.chatSenderId) {
      store.chatSenderId = queryParams.chatSenderId;
    }
    delete queryParams.openChat;
    delete queryParams.groupId;
    delete queryParams.timestamp;
    delete queryParams.id;
    delete queryParams.chatType;
    router.replace({ query: queryParams });
    return false;
  }
});

router.beforeEach(v2RedirectionNavGuard);
// crew guard

router.beforeEach(async (to, from, next) => {
  if (router?.publicRoutes?.includes(to.path)) {
    next();
    return;
  }

  const store = useRootStore();

  // Check if the user is logged in before checking if they're a crew member
  try {
    await Auth.currentAuthenticatedUser();
  } catch (_e) {
    // Not logged in, just move on
    next();
    return;
  }

  await store.waitForUserToBeLoaded();

  const base = import.meta.env.BASE_URL === '/' ? '' : import.meta.env.BASE_URL;
  const toPlusBase = `${base}${to.path}`;

  const isCommsHub = store.enterpriseClient === 'CommsHub';

  const shouldRedirectToCommsHub =
    isCommsHub &&
    ['/home-dashboard', '/', '/404'].includes(toPlusBase) &&
    to.path !== 'portal/commshub/home';

  if (shouldRedirectToCommsHub) {
    const params = new URLSearchParams(to.query);
    window.location.href = `/portal/commshub/home?${params.toString()}`;
    return;
  }

  if (store.isCrewMember) {
    const crewRoutes = [
      ...navItems.value.filter((item) => item.crewMenu).map((x) => x.path),
      '/user/account',
      '/admin/user/account',
      '/admin/alerts/settings',
      '/admin/alerts',
      '/portal/myq-messages',
      '/equipment/list',
      '/equipment/maintenance',
      '/equipment/tasks',
      '/equipment/tickets',
      '/equipment/parts',
      '/equipment/usageInsights/generalReporting',
    ];

    if (!crewRoutes.includes(toPlusBase)) {
      const params = new URLSearchParams(to.query);
      window.location.href = `/portal/feed?${params.toString()}`;
    }
  }
  next();
});

router.afterEach((to, from) => {
  // console.log('router.afterEach');
  const result = shouldRedirectToV2(to.path);
  if (!result.shouldRedirect) {
    unsetAuthTokenCookies();
    unsetAssumedUserOrgCookies();
  }
});

export default router;

function addRoute(route, parent) {
  if (parent) router.addRoute(parent.name, route);
  else router.addRoute(route);
  if (route.children) {
    route.children.forEach((child) => addRoute(child, route));
  }
}

export function addRoutes(routes) {
  routes.forEach((route) => addRoute(route));
}

export function addPublicRoutes(routes) {
  router.publicRoutes = [
    ...(router.publicRoutes ?? []),
    ...routes.map((route) => route.path),
  ];
}

const magicLinkRoute = {
  name: 'Magic Link Login',
  path: '/magicLink',
  meta: { id: 'magicLink' },
  component: () => import('../components/magicLink/magic_link_verify.vue'),
};

addRoute(magicLinkRoute);
addPublicRoutes([magicLinkRoute]);
